/**
 * Pintor do navegador. Primitivas -> SVG.
 *
 * Este arquivo **nao decide nada**. Recebe coordenadas em pontos, cor em RGB,
 * opacidade em numero e texto ja quebrado em linhas, e traduz para tags.
 *
 * Duas regras que a revisao de PR verifica:
 *
 * 1. **Nenhuma aritmetica de layout.** Se aparecer `*` ou `/` que nao seja
 *    conversao de cor, esta no arquivo errado — pertence a `layout.ts`. E o
 *    que garante que os dois pintores nao possam divergir: eles nao calculam.
 *
 * 2. **`switch` exaustivo com `assertNever`.** Uma sexta variante de Primitive
 *    quebra a compilacao aqui E no pintor de PDF, ao mesmo tempo.
 *
 * O contador no fim e a terceira rede: se um caso deixar de emitir, o total
 * nao bate e a funcao lanca. Um pintor que pula uma instrucao em silencio e
 * impossivel.
 */

import {
  assertNever,
  type Primitive, type ResolvedCertificate, type RGB, type FontRef,
} from '../types.js';

/** Resolve o id de um asset para algo que o `<img>`/`<image>` consiga carregar. */
export type AssetResolver = (id: string) => string;

/** Familias que o navegador usa. O SVG so pinta; a geometria ja veio pronta. */
const FAMILIA: Readonly<Record<FontRef, string>> = {
  'inter-400': "Inter, system-ui, sans-serif",
  'inter-600': "Inter, system-ui, sans-serif",
  'sora-700': "Sora, Inter, system-ui, sans-serif",
};

const PESO: Readonly<Record<FontRef, number>> = {
  'inter-400': 400,
  'inter-600': 600,
  'sora-700': 700,
};

/** RGB 0..1 -> `rgb(r,g,b)` 0..255. A unica conta permitida neste arquivo. */
function cor(c: RGB): string {
  const b = (v: number) => Math.round(v * 255);
  return `rgb(${b(c.r)},${b(c.g)},${b(c.b)})`;
}

/** Escapa o que quebraria o XML. */
function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Omite `opacity` quando e 1: menos ruido no diff dos goldens. */
function op(v: number): string {
  return v === 1 ? '' : ` opacity="${v}"`;
}

function pintar(p: Primitive, assets: AssetResolver): string {
  switch (p.k) {
    case 'rect':
      return `<rect x="${p.x}" y="${p.y}" width="${p.w}" height="${p.h}" fill="${cor(p.fill)}"${op(p.opacity)}/>`;

    case 'image':
      return `<image x="${p.x}" y="${p.y}" width="${p.w}" height="${p.h}" href="${esc(assets(p.asset))}" preserveAspectRatio="xMidYMid slice"${op(p.opacity)}/>`;

    case 'line':
      return `<line x1="${p.x1}" y1="${p.y1}" x2="${p.x2}" y2="${p.y2}" stroke="${cor(p.stroke)}" stroke-width="${p.w}"${op(p.opacity)}/>`;

    case 'poly':
      return `<polygon points="${p.pts.map(([x, y]) => `${x},${y}`).join(' ')}" fill="${cor(p.fill)}"${op(p.opacity)}/>`;

    case 'glyphs': {
      // `textLength` NAO e usado de proposito: ele faria o navegador esticar o
      // texto para bater com uma largura, escondendo divergencia de metrica em
      // vez de revelar. Se a fonte do navegador nao bater com a embutida no
      // PDF, e melhor que a comparacao por pixel acuse.
      const espaco = p.charSpacing === 0 ? '' : ` letter-spacing="${p.charSpacing}"`;
      return `<text x="${p.x}" y="${p.y}" font-family="${FAMILIA[p.font]}" font-size="${p.size}" font-weight="${PESO[p.font]}" fill="${cor(p.fill)}"${espaco}${op(p.opacity)} xml:space="preserve">${esc(p.text)}</text>`;
    }

    default:
      return assertNever(p);
  }
}

/**
 * Gera o SVG completo.
 *
 * `viewBox` em pontos: o mesmo espaco de coordenadas do PDF. Assim o SVG pode
 * ser exibido em qualquer tamanho sem que nada aqui precise saber disso.
 */
export function renderSvg(
  resolved: ResolvedCertificate,
  assets: AssetResolver,
): string {
  const { width, height } = resolved.page;

  const corpo: string[] = [];
  let pintadas = 0;
  for (const p of resolved.primitives) {
    corpo.push(pintar(p, assets));
    pintadas++;
  }

  if (pintadas !== resolved.primitives.length) {
    throw new Error(
      `renderSvg pintou ${pintadas} de ${resolved.primitives.length} primitivas. ` +
        'Nenhuma pode ser ignorada em silêncio.',
    );
  }

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" ` +
    `viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" ` +
    `font-kerning="none" style="shape-rendering:geometricPrecision">` +
    corpo.join('') +
    `</svg>`
  );
}
